import http from '@/api/http'
import { performerStatsPageLimit } from '@/config/api'
import { cursorParams, type PageMeta } from '@/api/pagination'

export interface PerformerStatsRole {
  // Role assigned to a performer, as embedded in GET `/persons/performers/stats`.

  name: string
  // Role name (e.g. `performer`, `client`, `owner`, `admin`).
}

export interface PerformerStatsPerson {
  // The performer identity fields used by the Stuff tab; the backend response
  // includes the full person payload (bills, diseases, etc.), but this view
  // only needs id/name/roles.

  id: string
  // Performer id (UUID7).

  name: string
  // Display name.

  roles: PerformerStatsRole[]
  // Assigned roles; always includes `performer`.
}

export interface PerformerBusinessService {
  // One business service occurrence within a performer's period breakdown.

  business_service_type_id: string
  // Reference to the service type definition (joined client-side against `/business-service-types`).

  price: number
  // Performer's earned price for this business service (already split across
  // performers server-side when more than one worked it).

  date: string
  // Calendar date (`YYYY-MM-DD`) the business service took place.

  male_clients_cnt: number
  // Number of distinct male clients who attended this business service.

  female_clients_cnt: number
  // Number of distinct female clients who attended this business service.

  group_id?: string | null
  // Id of the client group the business service was held for; absent/null for an ad-hoc service. Shown in the Stuff tab's Group / Age columns.
}

export interface PerformerStat {
  // Aggregated stats for one performer over the requested period.

  business_services_cnt: number
  // Count of business services in `business_services` below.

  total_income: number
  // Sum of `business_services[].price`.

  business_services: PerformerBusinessService[]
  // One entry per business service the performer conducted in the period.

  unique_clients_cnt: number
  // Count of distinct clients the performer served in the period.
}

export interface PerformerStatsItem {
  // One row from GET `/persons/performers/stats`.

  person: PerformerStatsPerson
  stat: PerformerStat
}

interface PerformerStatsResponse extends PageMeta {
  // Paginated list payload from GET `/persons/performers/stats`.

  items: PerformerStatsItem[]
  // Page of performer stats.
}

/**
 * Fetches a page of performer stats for an inclusive date range. Same
 * cursor/limit contract as `fetchClientVisitCountsByPeriod`; `start`/`end`
 * are ISO calendar dates (`YYYY-MM-DD`), both inclusive, matching the
 * backend's `dt.date` query params (`start_date`/`end_date`).
 */
export async function fetchPerformerStats(
  start: string,
  end: string,
  cursor?: number,
): Promise<PerformerStatsResponse> {
  const { data } = await http.get<PerformerStatsResponse>('/persons/performers/stats', {
    params: {
      start_date: start,
      end_date: end,
      ...cursorParams(performerStatsPageLimit, cursor),
    },
  })
  if (import.meta.env.DEV) {
    console.log('[fetchPerformerStats] GET /persons/performers/stats response', data)
  }
  return data
}
